import { Component, ReactNode } from 'react';
import { reportError } from '@/lib/errorReporter';
import { ErrorBoundaryFallback } from './ErrorBoundaryFallback';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  name?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorId: string;
}

function generateErrorId(): string {
  const ts = Date.now().toString(36);
  const rand = Math.random().toString(36).slice(2, 8);
  return `err_${ts}_${rand}`;
}

// Chunk-load error patterns the main.tsx recovery wrapper looks for. Kept in
// sync with the regex in src/main.tsx (handleChunkError). When a route module
// fails to load — stale session pointing at a deploy-rotated chunk hash,
// mobile network drop, transient CDN edge 404 — React's lazy() rejects the
// import promise, and ErrorBoundary catches it BEFORE it can bubble to the
// window's unhandledrejection listener. That meant the recovery reload in
// main.tsx silently never fired for these cases (reported pattern:
// err_mphgrg8n_hlwjmd, Wesley, designer, Android Chrome 147, 2026-05-23).
// Detecting the same pattern here and reloading inside componentDidCatch
// closes that gap. Same sessionStorage flag guards against infinite reload
// loops when the chunk is genuinely missing.
const CHUNK_ERROR_RE = /Failed to fetch dynamically imported module|Importing a module script failed|ChunkLoadError|Unable to preload CSS/i;

function isChunkLoadError(error: Error | null): boolean {
  return !!error && CHUNK_ERROR_RE.test(error.message || String(error));
}

function tryChunkRecovery(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    if (window.sessionStorage.getItem('chunk-error-reload') === '1') {
      return false; // already tried once this session; don't loop
    }
    window.sessionStorage.setItem('chunk-error-reload', '1');
    window.location.reload();
    return true;
  } catch {
    return false;
  }
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null, errorId: '' };

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Always mark hasError so React doesn't re-render the throwing child in a
    // loop. componentDidCatch handles chunk-load recovery by triggering a
    // reload before the fallback UI is meaningfully visible (no measurable
    // flash in practice — reload aborts pending paints).
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Chunk-load recovery: silently reload before reporting the error. The
    // new index.html points at current chunk hashes and the navigation
    // retries successfully. Only one reload attempt per session to avoid
    // looping when the chunk is genuinely missing.
    if (isChunkLoadError(error) && tryChunkRecovery()) {
      return;
    }
    const errorId = generateErrorId();
    this.setState({ errorId });
    const componentName = this.props.name || 'unknown';
    console.error(`[ErrorBoundary:${componentName}]`, errorId, error, info.componentStack);
    reportError(error, {
      component: `ErrorBoundary:${componentName}`,
      componentStack: info.componentStack,
      errorId,
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorId: '' });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <ErrorBoundaryFallback
          error={this.state.error}
          errorId={this.state.errorId}
          componentName={this.props.name || 'unknown'}
          onReset={this.handleReset}
        />
      );
    }
    return this.props.children;
  }
}
